import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {CSSProperties} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {WHITE} from 'ui-library/Colors';

import {ReviewBadgeStatus} from './ReviewConstants';

export interface ReviewActionButtonProps {
  mode: ReviewBadgeStatus;
  text: string | null;
  isDropdown: boolean;
  icon: JSX.Element;
  onClick?: () => void;
  defaultBadgeStyle?: CSSProperties;
  /** Whether the badge dropdown menu is currently open. Only meaningful when {@link isDropdown} is true. */
  expanded?: boolean;
  className?: string;
}

export function ReviewActionButton(props: ReviewActionButtonProps): JSX.Element {
  // use default color if review status in progress, otherwise use ReviewBadge set colors in REVIEW_BUTTON_STYLE_FOR_STATUS
  const badgeStyle =
    props.mode === ReviewBadgeStatus.IN_PROGRESS && props.defaultBadgeStyle
      ? props.defaultBadgeStyle
      : REVIEW_BUTTON_STYLE_FOR_STATUS[props.mode];
  return (
    <Button
      className={classNames(css(reviewButtonStyle.button, badgeStyle), props.className)}
      icon={props.icon}
      isDropdown={props.isDropdown}
      disabled={!props.isDropdown}
      text={props.text}
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.MEDIUM}
      onClick={props.onClick}
      // Expose the menu-button semantics so consumers (and tests) can detect the actionable dropdown via
      // ARIA rather than the presentational `dropdown-toggle` class.
      aria-haspopup={props.isDropdown ? 'menu' : undefined}
      aria-expanded={props.isDropdown ? Boolean(props.expanded) : undefined}
      dataTestAttrs={{component: 'ReviewActionButton', element: 'root'}}
    />
  );
}

export const reviewButtonStyle = StyleSheet.create({
  button: {
    borderRadius: 16,
    fontSize: 14,
    padding: '8px 12px',
    fontWeight: 500,
    minHeight: 32,
    height: 'auto',
    color: WHITE,
    cursor: 'default',
    ':hover:not(.is-disabled)': {cursor: 'pointer'},
  },
  loadingButton: {
    opacity: 0.5,
  },
});

export const reviewStyles = StyleSheet.create({
  accepted: {
    backgroundColor: Color.GREEN_600,
    ':hover:not(.is-disabled)': {backgroundColor: Color.GREEN_550},
    ':active:not(.is-disabled)': {backgroundColor: Color.GREEN_500},
  },
  needsReview: {
    backgroundColor: Color.ORANGE_350,
    ':hover:not(.is-disabled)': {backgroundColor: Color.ORANGE_300},
    ':active:not(.is-disabled)': {backgroundColor: Color.ORANGE_200},
    color: Color.GRAY_900,
  },
  inProgress: {
    backgroundColor: Color.BLUE_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.BLUE_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.BLUE_400},
  },
  redButton: {
    backgroundColor: Color.RED_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.RED_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.RED_400},
  },
});

export const REVIEW_BUTTON_STYLE_FOR_STATUS = {
  [ReviewBadgeStatus.ACCEPTED]: reviewStyles.accepted,
  [ReviewBadgeStatus.REJECTED]: reviewStyles.redButton,
  [ReviewBadgeStatus.IN_PROGRESS]: reviewStyles.inProgress,
  [ReviewBadgeStatus.IN_REVIEW]: reviewStyles.needsReview,
  [ReviewBadgeStatus.ACTION_REQUIRED]: reviewStyles.redButton,
};

export default ReviewActionButton;
